/**
 * RasterHeatmapGLLayer.ts
 * طبقة Heatmap من نسيج عالمي خام (GFS GRIB2 → PNG رمادي 1440×721، قيمة مُطبّعة) —
 * بدقّة 0.25° كاملة مثل Zoom Earth، بلا أي طلبات نقاط لـ Open-Meteo.
 *
 * الشيدر مطابق لـ HeatmapGLLayer (شريط ألوان + قناع ساحلي)، لكن مصدر القيم صورة عالمية
 * واحدة بدل شبكة نقاط. الحدود ثابتة عالمياً، والصفّ 0 من الصورة = الجنوب (يطابق v=0).
 */

import { MercatorCoordinate, type CustomLayerInterface, type CustomRenderMethodInput, type Map as MaplibreMap } from 'maplibre-gl';
import type { ForecastGridType } from '../../../config/weatherLayers';
import { type GLContext, createProgram, getLocations, createTexture, updateTexture, bindTexture } from './glUtils';
import { buildColorRamp } from './weatherTextures';
import { loadRasterImage, MissingRasterError } from '../../../services/rasterImageCache';
import { acquireLandMask, acquireGridMask, getLandMaskSize } from './landMaskTexture';
import { perfSection } from '../../../services/perfProbe';

const VERTEX_SRC = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
varying vec2 v_merc;
void main() { v_merc = a_pos; gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0); }`;

// مطابق لشيدر HeatmapGLLayer (قناع ساحلي) — أي تعديل لوني يبقى متوافقاً معه.
// يدعم الاستيفاء الزمني: يأخذ القيمة المنعّمة من إطارين (u_value, u_value2) ويمزجهما بـ u_blend
// قبل تمريرها لشريط الألوان — حركة ناعمة بين الساعات مثل Zoom Earth، بلا بيانات جديدة.
const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_merc;
uniform sampler2D u_value;
uniform sampler2D u_value2;
uniform sampler2D u_ramp;
uniform sampler2D u_mask;
uniform sampler2D u_gridMask;
uniform float u_useMask;
uniform float u_seaOnly;
uniform float u_noData;      // 1 = البايت 0 يعني «لا بيانات» فيُرفض البكسل
uniform float u_debug;       // 1 = وضع التشخيص: لوّن سببَ الرفض بدل إسقاط البكسل
uniform float u_landOnly;
uniform float u_blend;
uniform vec2 u_gridSize;
uniform float u_opacity;
uniform vec4 u_bounds;
uniform float u_zoom;
uniform float u_maskSize;
/**
 * u_p16: النسيج نظير 16-بت (‏R=البايت الأعلى، G=الأدنى) لا رمادي 8-بت.
 *
 * الفارق ليس ترفاً: 8 بت على مدى 95 هكتوباسكال = 0.373 لكل مستوى، أي انزياح عشرات
 * الكيلومترات لخطّ الضغط وتموّجٌ كامل في الحقول المسطّحة. والخطوط نفسها لم تعد تُرسم
 * هنا (راجع IsobarGLLayer — طبقة مستقلّة تعمل فوق أي حقل)، لكن دقّة **اللون** تستفيد
 * من القناتين كذلك: تدرّج الضغط عندنا أربع عشرة عتبة، وتكميم 0.373 يجعل العتبة تتذبذب.
 */
uniform float u_p16;
const float PI = 3.141592653589793;
float mercV(float latDeg){ float lat=radians(latDeg); return (1.0 - log(tan(PI*0.25+lat*0.5))/PI)*0.5; }

// أوزان Catmull-Rom تكعيبية (استيفاء حقيقي يمرّ بقيم الشبكة الفعلية بدل تقريبها
// كـ B-spline السابق الذي كان يذيب التفاصيل عند التكبير). لا حواف خلايا مهما بلغ
// التكبير، مع بقاء تفاصيل الحقل واضحة كـ Zoom Earth.
vec4 cubicW(float t) {
    float t2 = t*t, t3 = t2*t;
    return vec4(-0.5*t3 + t2 - 0.5*t,
                 1.5*t3 - 2.5*t2 + 1.0,
                -1.5*t3 + 2.0*t2 + 0.5*t,
                 0.5*t3 - 0.5*t2);
}

/**
 * فكّ قيمة الخليّة من بكسل النسيج.
 *
 * الرمادي 8-بت يعطي «R»؛ ونظير 16-بت يعطي «(R*256 + G)/65535». الطرفان مُطبَّعان
 * على المدى نفسه بالضبط (‏255*257 = 65535)، فشريط الألوان لا يعرف أيّهما قُرئ —
 * ولا فرع في أي مكان آخر من الشيدر.
 */
float dec(vec4 c) {
    return mix(c.r, (c.r * 65280.0 + c.g * 255.0) / 65535.0, u_p16);
}

/**
 * تصنيف البكسل يابسةً/بحراً باستيفاء تكعيبي، فيبقى حدّ اللون
 * وخطّ الساحل المرسوم **منحنىً واحداً** لا منحنيين متقاربين.
 *
 * القناع بدقّة زوم 3 (‏4096 = 512·2³)، فتكسله يغطّي 2^(z−3) بكسل شاشة. مع ثنائية
 * الخطّية تصير مجموعة المستوى 0.5 سلسلةَ قطع مستقيمة ⇒ ساحل مضلّع بزوايا حادّة عند
 * التكبير، وهو ما ظهر كحدود «مكسّرة» ووتد لوني حادّ الحافة.
 *
 * الثمن 16 قراءة، ولذلك **يُدفع عند الساحل وحده**: بعيداً عنه تكون التغطية 0 أو 1
 * تماماً فتكفي قراءة واحدة. الفرع متماسك مكانياً (مساحات كاملة كلّها برّ أو كلّها بحر)
 * فلا يتشعّب داخل الـwarp إلّا على شريط الساحل — بخلاف sameFill السابق الذي كان يُجري
 * 49 دورة لكل بكسل في المشهد بلا استثناء.
 */
float landAt(vec2 m) {
    float c = texture2D(u_mask, m).r;
    if (c < 0.002) return 0.0;
    if (c > 0.998) return 1.0;
    float px = 1.0 / u_maskSize;
    float sx = m.x*u_maskSize - 0.5, sy = m.y*u_maskSize - 0.5;
    float ix = floor(sx), iy = floor(sy);
    vec4 wx = cubicW(sx - ix), wy = cubicW(sy - iy);
    float s = 0.0;
    for (int j=0;j<4;j++){ for (int i=0;i<4;i++){
        vec2 t = vec2((ix + float(i) - 0.5) * px, clamp((iy + float(j) - 0.5) * px, 0.0, 1.0));
        s += texture2D(u_mask, vec2(fract(t.x), t.y)).r * wx[i] * wy[j];
    }}
    return step(0.5, s);
}

/**
 * الاحتياطي حين لا تحوي نافذة الـ4×4 أي عيّنة من جانب البكسل (جزيرة أو خليج أضيق من
 * خليّة الشبكة): متوسّط كل العيّنات.
 *
 * كان هنا بحث أوسع (sameFill) يمسح 7×7 خليّة ليأتي بقيمة من نفس الجانب فقط. كان يعمل —
 * قِيس ونجح على لسان أضيق من خليّة — لكن كلفته **98 قراءة نسيج لكل بكسل**، أغلبها
 * قراءات متناثرة في قناع 64 ميغابايت تُحطّم ذاكرة النسيج المؤقّتة. وحلقة GLSL لا تُختصر
 * بـ continue: الـ49 دورة تُنفَّذ، والتشعّب داخل الـwarp يدفع الجميع ثمنها. النتيجة كانت
 * تجمّد العرض فور ظهور طبقة اللون — وهو بالضبط ما رصده المستخدم.
 *
 * الفائدة كانت محصورة في ألسنة ساحلية أضيق من خليّة الشبكة (22 كم)، والثمن كان الأداء
 * كلّه. أُزيل عمداً؛ إن عادت الحاجة فالمسار الصحيح تجهيز الحقل على الخادم لا مسح الجوار
 * في الشيدر لكل بكسل.
 */
float blendSides(float sameSum, float sameW, float allSum, float allW) {
    if (sameW > 0.001) return sameSum / sameW;
    return allW > 0.0 ? allSum / allW : -1.0;
}

/**
 * إحداثيّ عيّنة **ملفوف بخطّ الطول ومقصوص بخطّ العرض**.
 *
 * النسيج عالميّ يلتفّ حول الكرة، فجارُ العمود الأول هو العمود الأخير لا نفسه. وكان
 * القصّ (clamp) يُطبَّق على المحورين معاً، فعند خطّ التاريخ (u→0 أو 1) تُستبدل عيّنات
 * الجانب الآخر بتكرار عمود الحافّة: يُسطَّح الحقل في شريط ضيّق ويختلف الجانبان، فيظهر
 * حدّ رأسيّ واضح حيث تلتقي نسخة العالم بالنسخة التالية — وهو أوضح ما يكون حين يقع
 * إعصار على الخطّ نفسه فيبدو نصفه مقطوعاً عن نصفه.
 *
 * الحلّ أن يلتفّ x كما تلتفّ الكرة (fract) ويبقى y مقصوصاً (القطبان لا يلتفّان).
 */
vec2 wrapUV(vec2 uv) {
    return vec2(fract(uv.x), clamp(uv.y, 0.0, 1.0));
}

/* ── تمويه ثنائي المقياس للكنتور (‏دالّة «p» في شيدر Zoom Earth) ───────────────
 *
 * الخطوط تُشتقّ من حقلٍ **غير الحقل الملوَّن**: نواة غاوسية على شبكة 9×9 خطوة،
 * تُقرأ مرّتين — نواة ضيّقة (المركز + الحلقة الأولى) وأخرى عريضة (الشبكة كلّها).
 *
 * ولماذا اثنتان بدل واحدة: الفرق بينهما هو **حدّة** الحقل محلياً. تُعاد إضافته
 * مضروباً في 1.315 حيث يكون كبيراً (‏unsharp mask) فتبقى نواة المنخفض المداري —
 * وهي بضع خلايا عرضاً — حادّةً بما يكفي لتُغلق حولها حلقات؛ وتُترك حيث يكون
 * صغيراً فيُمحى ضجيج التكميم في الحقول المسطّحة. تمويهٌ واحد كان يفعل أحدهما
 * على حساب الآخر دائماً.
 */
// القيمة المنعّمة لإطار واحد عند (uv). يعيد سالباً لو لا وزن (يُستخدم للتجاهل).
float smoothVal(sampler2D tex, vec2 uv, vec2 gs, float pixelLand) {
    float sx = uv.x*gs.x - 0.5, sy = uv.y*gs.y - 0.5;
    float ix = floor(sx), iy = floor(sy);
    vec4 wx = cubicW(sx - ix), wy = cubicW(sy - iy);
    // فصل صارم عند الساحل: عيّنات النوع المطابق (يابسة/بحر) فقط؛ المخالف صفر —
    // فلا يتسرّب لون اليابسة الحارّة إلى البحر. الاحتياطي (كل العيّنات) يُستخدم
    // فقط حين لا توجد أي عيّنة مطابقة في النافذة (جزر/خلجان أصغر من خلية الشبكة).
    float sameSum = 0.0, sameW = 0.0, allSum = 0.0, allW = 0.0;
    // مدى العيّنات المساهِمة — للتقييد أدناه (بيكيوبك مقيَّد).
    float sameLo = 1.0, sameHi = 0.0, allLo = 1.0, allHi = 0.0;
    for (int j=0;j<4;j++){ for (int i=0;i<4;i++){
        float w = wx[i] * wy[j];
        vec2 cuv = wrapUV(vec2((ix + float(i) - 0.5)/gs.x, (iy + float(j) - 0.5)/gs.y));
        float val = dec(texture2D(tex, cuv));
        allSum += val * w; allW += w;
        allLo = min(allLo, val); allHi = max(allHi, val);
        // تصنيف العيّنة من قناع بدقّة الشبكة وبنفس إسقاطها: قراءة واحدة بإحداثيّ
        // العيّنة نفسه — بلا تحويل مركاتور (log/tan) وبلا قفز في قناع 64 ميغابايت.
        if (u_useMask > 0.5 && step(0.5, texture2D(u_gridMask, cuv).r) != pixelLand) w = 0.0;
        sameSum += val * w; sameW += w;
        if (w != 0.0) { sameLo = min(sameLo, val); sameHi = max(sameHi, val); }
    }}
    float r = blendSides(sameSum, sameW, allSum, allW);
    if (r < 0.0) return r;                       // إشارة "لا وزن" — تُمرَّر كما هي
    // **تقييد بمدى الجوار.** لأوزان Catmull-Rom فصّان سالبان، فعند قمّة حادّة تتجاوز
    // النتيجةُ كل قيم الشبكة المحيطة: قمّة 120 كم/س تُرسم 130 في بُعد و141 في بُعدين.
    // يظهر ذلك عند الأعاصير تحديداً (نصف قطر أقصى رياح 30–50 كم مقابل خليّة 27.8 كم).
    // يجب أن يبقى مطابقاً لـ bicubic في rasterSampler.ts وإلّا افترق رقم التلميح عن اللون.
    if (sameW > 0.001) return clamp(r, sameLo, sameHi);
    return clamp(r, allLo, allHi);
}

// نسخة ثنائية الخطّية أرخص (4 عيّنات بدل 16) — تُستخدم فقط أثناء المزج الزمني النشط
// (u_blend > 0)، حين يُستدعى smoothVal مرّتين لكل بكسل بالفعل (إطارين). التبديل
// لثنائية الخطّية هناك يُبقي الحمل قريباً من حالة السكون بدل مضاعفته أثناء السحب/التشغيل،
// وفرق النعومة غير محسوس عملياً لأنه يظهر لأقل من نصف ثانية بين كل خطوتَي 10 دقائق.
float bilinearVal(sampler2D tex, vec2 uv, vec2 gs, float pixelLand) {
    vec2 s = uv * gs - 0.5;
    vec2 i = floor(s);
    vec2 f = s - i;
    float sameSum = 0.0, sameW = 0.0, allSum = 0.0, allW = 0.0;
    for (int j = 0; j < 2; j++) { for (int k = 0; k < 2; k++) {
        float w = (k == 0 ? (1.0 - f.x) : f.x) * (j == 0 ? (1.0 - f.y) : f.y);
        vec2 cuv = wrapUV(vec2((i.x + float(k) + 0.5) / gs.x, (i.y + float(j) + 0.5) / gs.y));
        float val = dec(texture2D(tex, cuv));
        allSum += val * w; allW += w;
        // تصنيف العيّنة من قناع بدقّة الشبكة وبنفس إسقاطها: قراءة واحدة بإحداثيّ
        // العيّنة نفسه — بلا تحويل مركاتور (log/tan) وبلا قفز في قناع 64 ميغابايت.
        if (u_useMask > 0.5 && step(0.5, texture2D(u_gridMask, cuv).r) != pixelLand) w = 0.0;
        sameSum += val * w; sameW += w;
    }}
    return blendSides(sameSum, sameW, allSum, allW);
}

void main() {
    // الطول يُلَفّ لنسخة العالم الواحدة (fract) فتظهر الطبقة في كل النسخ المكرّرة أثناء التحريك.
    float u = fract(v_merc.x);
    float latRad = 2.0 * atan(exp(PI * (1.0 - 2.0 * v_merc.y))) - PI * 0.5;
    float lat = degrees(latRad);
    float v = (lat - u_bounds.y) / (u_bounds.w - u_bounds.y);
    if (v < 0.0 || v > 1.0) discard;              // خط العرض فقط (الطول ملفوف)
    vec2 gs = u_gridSize;
    float pixelLand = landAt(vec2(u, v_merc.y));   // قناع البرّ ملفوف كذلك
    // تمريرة "البحر فقط": نسخة ثانية من نفس الطبقة تُرسم **فوق** التضاريس فتُعيد طلاء
    // الماء بلونه الأصلي، فلا يظهر ظلّ التضاريس إلا على اليابسة (بلاطات terrarium تحمل
    // أعماق البحار، وطبقة hillshade في MapLibre لا تقبل قناعاً). الرفض هنا مبكّر — قبل
    // الـ16 عيّنة — فالتكلفة الإضافية تقتصر على بكسلات الماء وحدها.
    if (u_seaOnly > 0.5 && pixelLand > 0.5) {
        // ماجنتا = قناع البرّ هو من رفض هذا البكسل.
        if (u_debug > 0.5) { gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); return; }
        discard;
    }
    // والعكس في التمريرة الأولى: ما دامت تمريرة الماء ستُعيد رسم البحر فوق التضاريس،
    // فرسمه هنا أيضاً عملٌ مهدور. بهذا يبقى مجموع العمل = بكسل واحد مرّة واحدة تماماً
    // كما قبل إضافة التضاريس، لا مرّتين. (الرايتان تشتركان في شرط جهوز القناع، فلا
    // يمكن أن يسقط البحر بين التمريرتين فيترك فجوة.)
    if (u_landOnly > 0.5 && pixelLand < 0.5) {
        // أصفر = تمريرة اليابسة أسقطت البكسل (يُنتظر أن ترسمه تمريرة البحر).
        if (u_debug > 0.5) { gl_FragColor = vec4(1.0, 1.0, 0.0, 1.0); return; }
        discard;
    }

    // نفس بيكيوبك Catmull-Rom لكل الطبقات (ساحلية وغير ساحلية) — يمرّ بقيم الشبكة
    // الفعلية بالضبط بدل تنعيمها/تمويهها كما كان بيكيوبك B-spline السابق يفعل لغير
    // الساحلية، فتظهر التفاصيل الحقيقية للحقل أوضح (كـ Windy) بدل حقل "مطموس" رغم أن
    // البيانات فعلاً تحوي تبايناً أكبر. أثناء المزج الزمني (تبديل إطارَي ساعة) نكتفي
    // بثنائي الخطّية الأرخص عند الزووم المنخفض — فرق النعومة غير محسوس لأنه يظهر أقل
    // من نصف ثانية، ونتفادى مضاعفة 16 عيّنة إلى 32 لكل بكسل بلا داعٍ.
    bool hq = u_zoom > 5.0;
    float val;
    if (u_useMask < 0.5) {
        // غير ساحلية (رياح/ضغط/سحب/أمطار...): لا قناع (pixelLand غير مستخدَم لأن
        // u_useMask=0 يُسقط فرع الفصل الساحلي داخل smoothVal/bilinearVal تلقائياً).
        if (u_blend > 0.001) {
            float a = hq ? smoothVal(u_value, vec2(u,v), gs, pixelLand) : bilinearVal(u_value, vec2(u,v), gs, pixelLand);
            float b = hq ? smoothVal(u_value2, vec2(u,v), gs, pixelLand) : bilinearVal(u_value2, vec2(u,v), gs, pixelLand);
            val = mix(a, b, u_blend);
        } else {
            val = smoothVal(u_value, vec2(u,v), gs, pixelLand);
        }
    } else {
        // ساحلية (حرارة/رطوبة...): جانب واحد لكل بكسل، بعتبة على تغطية القناع.
        // كان هنا تقييم مزدوج (مرّة كيابسة ومرّة كبحر) للمزج داخل شريط انتقالي — أُضيف
        // لإزالة سلّم التكسلات، ثم عولج السلّم من جذره بترميز **تغطية** في القناع نفسه
        // (المولّد يُفرط العيّنات ×4 ثم يُنعّم). فبقي التقييم المزدوج يضاعف عمل الشيدر
        // بلا مقابل: العتبة على حقل ناعم تعطي حدّاً ناعم الشكل أصلاً — وحادّاً كما طُلب.
        // نفس تصنيف pixelLand أعلاه بالضبط — حُسِب مرّةً واحدة بدل قراءة ثانية للقناع
        // بنفس الإحداثي. أي اختلاف بين الاثنين كان سيجعل البكسل يُصنَّف برّاً لاختيار
        // العيّنات وبحراً لتمريرة الرسم، فيظهر شريط فارغ على الساحل.
        float pl = pixelLand;
        float a = hq ? smoothVal(u_value, vec2(u,v), gs, pl) : bilinearVal(u_value, vec2(u,v), gs, pl);
        if (a < 0.0) discard;
        if (u_blend > 0.001) {
            float b = hq ? smoothVal(u_value2, vec2(u,v), gs, pl) : bilinearVal(u_value2, vec2(u,v), gs, pl);
            a = b >= 0.0 ? mix(a, b, u_blend) : a;
        }
        val = a;
    }
    /*
     * ── رفض «لا بيانات» ────────────────────────────────────────────────────
     *
     * الاختبار على val **بعد** الاستيفاء لا على عيّنة نقطية قبله. المحاولة الأولى
     * كانت تحسب إحداثيّ نسيج بيدها (‏(u*gs-0.5)/gs) لتقرأ الخليّة الخام؛ وكانت تلك
     * الإحداثيات مزاحة عن اتفاقية smoothVal — فرُفضت بكسلات محيط سليمة تماماً
     * وظهرت مساحات قاعدة كحلية واسعة في جنوب الأطلسي والمحيط الهندي. الدرس: لا
     * تُعِد اشتقاق إحداثيات النسيج يدوياً ما دام في الشيدر مسارٌ صحيح أصلاً.
     *
     * والاختبار على القيمة المستوفاة آمن هنا تحديداً لأن المولّد يترك حزاماً بعرض
     * ١٢ خليّة من القيم الحقيقية حول كل حافّة بيانات (‏FILL_CELLS)، ونصف نافذة
     *
     * (تنبيه لمن يحرّر تعليقات هذا الشيدر: لا تستعمل علامة الاقتباس المائلة التي
     *  تُحيط بالشيفرة في ماركداون. النصّ كلّه داخل template literal في JS، فأيّ
     *  علامة منها تُنهي السلسلة وتُسقط الملفّ بمئة خطأ نحويّ بعيد عن موضع السبب.)
     * الاستيفاء خليّتان — فأي بكسل تُخالط نافذتَه خليّةُ «لا بيانات» يكون أصلاً في
     * عمق اليابسة أو الجليد، لا على ساحل. أي أن المزج بين الحالتين لا يقع على أي
     * حافّة يراها المستخدم.
     *
     * العتبة نصف خطوة ترميز: القيم الحقيقية تبدأ من البايت 1، و«لا بيانات» هو 0.
     */
    if (u_noData > 0.5 && val < 0.5 / 255.0) {
        // أخضر = علامة «لا بيانات» في الراستر هي من رفض هذا البكسل.
        if (u_debug > 0.5) { gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0); return; }
        discard;
    }
    gl_FragColor = texture2D(u_ramp, vec2(val, 0.5)) * u_opacity;
}`;

/**
 * وضع تشخيص مؤقّت لطبقة شذوذ سطح البحر — يُفعَّل بـ `?sstdebug=1` في الرابط فقط.
 *
 * سببه أن السؤال «لماذا يظهر بحرٌ صحيح القيمة أسودَ؟» لا يُجاب إلا برؤية **أي فرعِ
 * رفضٍ** أسقط البكسل، وذلك غير مرئي من خارج الـGPU: كل الفروع تنتهي بـdiscard فتُنتج
 * النتيجة نفسها بالضبط (قاعدة الخريطة) مهما اختلف سببها.
 *
 * في هذا الوضع يُلوَّن السبب بدل إسقاط البكسل:
 *   ماجنتا = قناع البرّ رفضه   |   أخضر = علامة «لا بيانات»   |   أصفر = تمريرة اليابسة
 *
 * وهو خامل تماماً بلا الرايةpath: قراءة واحدة من الرابط عند تحميل الوحدة، وuniform
 * قيمته صفر — فلا أثر له في الإنتاج.
 */
let sstDebug: boolean | null = null;
function sstDebugEnabled(): boolean {
    if (sstDebug === null) {
        try { sstDebug = new URLSearchParams(window.location.search).get('sstdebug') === '1'; }
        catch { sstDebug = false; }
    }
    return sstDebug;
}

const LAT_LIMIT = 85.051129;

/** مدخل حلقة النُسج: نسيج على الـ GPU مع أبعاده وحالة جهوزه. */
interface PoolEntry {
    tex: WebGLTexture;
    w: number;
    h: number;
    /** true بعد نجاح texImage2D — قبلها النسيج 1×1 شفّاف ولا يصلح للرسم. */
    ready: boolean;
    /** جيل الطلب الجاري؛ أي وصول بجيل أقدم يُهمَل (وصول خارج الترتيب على شبكة بطيئة). */
    gen: number;
}

/** سعة حلقة النُسج لكل طبقة (‏~4 ميغابايت لكل نسيج). */
/**
 * رابط نظير المعاينة لملفّ راستر.
 *
 * يُدرج `.preview` قبل الامتداد مع الحفاظ على الاستعلام (`?v=run_epoch`) كما هو —
 * الترقيم هو ما يُبطل الكاش عند دورة جديدة، وإسقاطه يعني خدمة معاينة قديمة إلى الأبد.
 *
 * المفاتيح الصناعية (`mem:`) تُبنى في الذاكرة ولا نظير لها على الشبكة، فتُستثنى.
 */
/**
 * مجلّدات ثبت أنها بلا معاينات — نتوقّف عن طلبها منها بعد أوّل 404.
 *
 * المعاينة تحسينٌ اختياري: نموذجٌ لم تُولَّد له بعد (أو عُطِّل التوليد فيه) يجب أن يعمل
 * تماماً كما كان، **وبلا طلب واحد ضائع**. وبلا هذا الحرس كان كل إطار يُطلق طلباً يعود
 * بـ404: رحلة شبكة كاملة لكل ساعة، وعشرات الأسطر الحمراء في الطرفية تُخفي أخطاء حقيقية.
 *
 * الحارس على مستوى **المجلّد** لا الرابط: غياب معاينة إطار واحد يعني عملياً غيابها عن
 * النموذج كلّه (تُولَّد دفعةً واحدة)، فطلبٌ فاشل واحد يكفي للحكم. والذاكرة تُمسح مع
 * تحديث الصفحة، فتُلتقط المعاينات الجديدة في أوّل زيارة بعد توليدها.
 */
const dirsWithoutPreview = new Set<string>();

function dirOf(src: string): string {
    const q = src.indexOf('?');
    const path = q < 0 ? src : src.slice(0, q);
    return path.slice(0, path.lastIndexOf('/') + 1);
}

function previewOf(src: string): string {
    if (!src || src.startsWith('mem:')) return '';
    if (dirsWithoutPreview.has(dirOf(src))) return '';
    const q = src.indexOf('?');
    const path = q < 0 ? src : src.slice(0, q);
    const query = q < 0 ? '' : src.slice(q);
    const dot = path.lastIndexOf('.');
    if (dot < 0) return '';
    return `${path.slice(0, dot)}.preview${path.slice(dot)}${query}`;
}

const POOL_MAX = 8;

export class RasterHeatmapGLLayer implements CustomLayerInterface {
    public readonly id: string;
    public readonly type = 'custom' as const;
    public readonly renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private program: WebGLProgram | null = null;
    private loc: ReturnType<typeof getLocations> | null = null;
    private quadBuffer: WebGLBuffer | null = null;
    /**
     * حلقة نُسج مفهرسة بالرابط — النسيج يبقى على الـ GPU بعد أن يخرج من العرض.
     *
     * كان هناك نسيجان ثابتان فقط (الحالي والتالي) مع حيلة تبديل عند التقدّم للأمام. تلك
     * الحيلة تُغطّي اتجاهاً واحداً فقط: كل خطوة **للخلف** على الشريط، وكل قفزة إلى ساعة
     * زارها المستخدم للتوّ، كانت تُعيد رفع 1440×721 (‏~4 ميغابايت) إلى الـ GPU من جديد
     * رغم أن البكسلات نفسها ما زالت في كاش الصور المفكوكة. رفعٌ بهذا الحجم على الخيط
     * الرئيسي يكلّف بضعة مللي — وهو ما يُحسّ تلعثماً عند السحب ذهاباً وإياباً تحديداً.
     *
     * بالحلقة يصير الرجوع إلى ساعة قريبة **صفر عمل**: مجرّد ربط نسيج موجود. والحيلة
     * القديمة صارت زائدة (الحالة العامّة تشملها) فأُزيلت.
     *
     * السعة 8: تغطّي نافذة ±3 ساعات حول الموضع لطبقتين معاً. الكلفة ~32 ميغابايت VRAM.
     */
    private pool = new Map<string, PoolEntry>();
    private rampTexture: WebGLTexture | null = null;
    private maskTexture: WebGLTexture | null = null;
    private gridMaskTexture: WebGLTexture | null = null;
    private maskReady = false;
    /**
     * الرابط المعروض لحظة تبديل النوع — يُحجب الرسم ما دام `url` لم يتجاوزه بعد.
     * راجع setType: يمنع رسم نسيج النوع القديم بشريط ألوان النوع الجديد.
     */
    private typeGuardUrl: string | null = null;
    /**
     * الرابط المرسوم فعلاً الآن — قد يتأخّر عن `url` ريثما يصل إطاره.
     * وجوده هو ما يمنع اختفاء الطبقة أثناء الانتظار (راجع renderInner).
     */
    private displayedUrl: string | null = null;
    private destroyed = false;
    // عدّاد أجيال التحميل: على شبكة بطيئة قد تصل صورة طلبٍ قديم **بعد** الأحدث (وصول خارج
    // الترتيب) فتُكتب فوق نسيج لم يعد يخصّها. كل مدخل في الحلقة يحمل جيله، وأي وصول بجيل
    // لا يطابق مدخله الحالي يُهمَل بصمت.
    private loadGen = 0;

    /**
     * يُستدعى إن ثبت (404) أن الإطار المعروض غير موجود **قبل** أن تُظهر الطبقة أي إطار —
     * أي أن الطبقة بلا بيانات على الخادم أصلاً، فيعود المستدعي إلى الطبقة الافتراضية
     * بدل خريطة فارغة. ساعةٌ ناقصة بعد ظهور الطبقة لا تستدعيه (يبقى الإطار السابق).
     */
    onUnavailable?: () => void;

    private layerType: ForecastGridType;
    private opacity: number;
    private url: string;
    private url2 = '';
    /**
     * رابط معاينة الإطار الحالي — نظير مصغّر ثمانيةَ أضعاف يكتبه المولّد بجانب كل راستر
     * (`<name>.preview.webp`). قِيس على ICON: 18 كيلوبايت مقابل 728 للأصل، أي أصغر 40×.
     *
     * الغرض **زمن أوّل لون، لا الجودة**: يظهر الحقل خلال جزء من الثانية بدل ثانيتين، ثم
     * يحلّ الأصل كامل الدقّة محلّه فور وصوله. الصورة النهائية لا تتغيّر بتّة.
     */
    private previewUrl = '';
    private blend = 0;
    private rampDirty = true;
    // مدى قيم ترميز النسيج إن خالف VALUE_RANGES (سقوط الرياح إلى قناة R المرمَّزة 0..120).
    private valueRange: [number, number] | undefined;

    setValueRange(range: [number, number] | undefined) {
        if (range?.[0] === this.valueRange?.[0] && range?.[1] === this.valueRange?.[1]) return;
        this.valueRange = range;
        this.rampDirty = true;
        this.map?.triggerRepaint();
    }

    /** يُستدعى عند تغيّر نقاط الألوان زمن التشغيل (مثل تعديل الأدمن الحيّ) — يعيد بناء
     * شريط الألوان على GPU من STOPS_REGISTRY الحالي بدل الاحتفاظ بالنسخة الأولى للأبد. */
    markColorsDirty() {
        this.rampDirty = true;
        this.map?.triggerRepaint();
    }

    constructor(id: string, layerType: ForecastGridType, url: string, opacity = 0.9) {
        this.id = id;
        this.layerType = layerType;
        this.url = url;
        this.opacity = opacity;
    }

    setUrl(url: string) {
        if (url === this.url) return;
        this.url = url;
        // لم تعد هناك حيلة «تبديل النسيجين»: الحلقة تشمل الحالة العامّة. إن كان الرابط
        // مرفوعاً مسبقاً (تقدّم للأمام، أو رجوع لساعة قريبة) فـ ensure تُرجعه فوراً بلا
        // أي عمل؛ وإن لم يكن، تبدأ تحميله وتُبقي الإطار السابق معروضاً حتى يجهز.
        // رابط فارغ = «الإطار قيد البناء» (نسيج تراكم الأمطار قبل جهوزه). لا نجلب شيئاً؛
        // يُبقي renderInner آخرَ إطار جاهز معروضاً حتى يصل الرابط الحقيقي.
        // المعاينة تُطلب مع الأصل لا بدلاً منه: كلاهما ينطلق الآن، ويفوز من يصل أوّلاً.
        // فارق الحجم (40×) يجعل المعاينة تسبق دائماً على أي وصلة بطيئة، وتصل متأخّرةً بلا
        // ضرر على وصلة سريعة لأن الأصل يكون قد سبقها فلا تُعرض أصلاً.
        this.previewUrl = previewOf(url);
        if (this.map && url) {
            const entry = this.ensure(this.mapGL(), url);
            // المعاينة تُطلب **فقط** حين لا يكون الأصل جاهزاً بالفعل. الإطار المُحمّى
            // مسبقاً (وهو الغالب أثناء التصفّح العادي) يُعرض فوراً، فطلب معاينته يعني
            // بايتات على الشبكة لصورةٍ لن تُرى. وهذا أيضاً يُبقي الطلبات صفراً على
            // النماذج التي لم تُولَّد لها معاينات بعد.
            if (this.previewUrl && !entry.ready) this.ensure(this.mapGL(), this.previewUrl);
        }
        this.map?.triggerRepaint();
    }
    // الإطار التالي ونسبة المزج للاستيفاء الزمني. blend=0 → الإطار الحالي فقط.
    setNextUrl(url: string) {
        if (url === this.url2) return;
        this.url2 = url;
        if (this.map && url) this.ensure(this.mapGL(), url);
    }
    setBlend(b: number) { const nb = Math.max(0, Math.min(1, b)); if (nb !== this.blend) { this.blend = nb; this.map?.triggerRepaint(); } }
    setType(t: ForecastGridType) {
        if (t === this.layerType) return;
        this.layerType = t;
        this.rampDirty = true;
        /*
         * نوع جديد = بيانات جديدة كلياً. النسيج المعروض يخصّ النوع القديم (مثلاً ضغط)، فلو
         * استمر رسمه بشريط ألوان النوع الجديد (مثلاً أمطار) قبل وصول صورة النوع الجديد،
         * ظهرت ألوان عشوائية تغطّي الخريطة حتى ينتهي التحميل — وميض واضح عند كل تبديل طبقة.
         *
         * مع الحلقة صار الحجب ببصمة الرابط لا براية عامّة: نُسجّل الرابط المعروض لحظة
         * التبديل، ويتوقّف الرسم ما دام `this.url` لم يتغيّر عنه بعد. سبب هذا الشكل أن
         * setType و setUrl يصلان من مؤثّرين منفصلين في React بلا ترتيب مضمون؛ راية عامّة
         * كانت ستُطفأ بأول تحميل ينتهي أياً كان نوعه.
         */
        this.typeGuardUrl = this.url;
        // الإطار المعروض يخصّ النوع السابق — لا يجوز إبقاؤه بديلاً أثناء الانتظار، فشريط
        // ألوان النوع الجديد سيُلوّن قيمه بمعنى مختلف كلياً (ضغط بألوان أمطار مثلاً).
        this.displayedUrl = null;
        this.map?.triggerRepaint();
    }
    setOpacity(o: number) { this.opacity = o; this.map?.triggerRepaint(); }

    /** النسيج المعروض نظير 16-بت؟ (يغيّر فكّ القيمة فقط — المدى نفسه). */
    private p16 = false;
    setP16(v: boolean) { if (this.p16 === v) return; this.p16 = v; this.map?.triggerRepaint(); }

    private glRef: GLContext | null = null;
    private mapGL(): GLContext { return this.glRef!; }

    onAdd(map: MaplibreMap, gl: GLContext) {
        this.map = map;
        this.glRef = gl;
        this.program = createProgram(gl, VERTEX_SRC, FRAGMENT_SRC);
        this.loc = getLocations(gl, this.program, ['a_pos'],
            ['u_matrix', 'u_value', 'u_value2', 'u_ramp', 'u_mask', 'u_gridMask', 'u_useMask', 'u_seaOnly', 'u_noData', 'u_debug', 'u_landOnly', 'u_blend', 'u_gridSize', 'u_opacity', 'u_bounds', 'u_zoom', 'u_maskSize', 'u_p16']);
        this.rampTexture = createTexture(gl, 256, 1, null, { filter: gl.LINEAR });
        // نسيج مشترك بقناة واحدة (راجع landMaskTexture) — لا تملكه هذه الطبقة ولا تحذفه.
        this.maskTexture = acquireLandMask(gl, () => { this.maskReady = true; this.map?.triggerRepaint(); });
        this.gridMaskTexture = acquireGridMask(gl, () => { this.map?.triggerRepaint(); });

        // رباعي يغطّي عدّة نسخ عالم أفقياً (مركاتور x من -N إلى 1+N) كي تظهر الطبقة في كل
        // النسخ المكرّرة (renderWorldCopies) أثناء التحريك بلا فراغات. الطول يُلَفّ في الشيدر
        // (fract) فتُعاد عيّنة النسيج العالمي لكل نسخة. y من نسخة واحدة (خط العرض لا يتكرّر).
        const yN = MercatorCoordinate.fromLngLat({ lng: 0, lat: LAT_LIMIT }).y;
        const yS = MercatorCoordinate.fromLngLat({ lng: 0, lat: -LAT_LIMIT }).y;
        const N = 3;                       // 3 نسخ لكل جانب (7 إجمالاً) تكفي حتى أدنى تكبير
        const x0 = -N, x1 = 1 + N;
        this.quadBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([x0, yN, x0, yS, x1, yN, x1, yS]), gl.STATIC_DRAW);

        if (this.url) this.ensure(gl, this.url);
        if (this.url2) this.ensure(gl, this.url2);
    }

    /** يُستدعى مرّة واحدة عند جهوز أوّل نسيج قيم (أول رسم صحيح للطبقة) — لتنعيم كشفها. */
    public onFirstFrame?: () => void;
    private firstFrameFired = false;

    /**
     * يضمن وجود نسيج جاهز للرابط في الحلقة، ويُعيده.
     *
     * إن كان موجوداً: عمل صفر — مجرّد تحديث ترتيب الاستعمال. وهذه هي الحالة الشائعة أثناء
     * التشغيل (الإطار التالي أُحمي مسبقاً) وأثناء السحب داخل نافذة الساعات المزارة.
     * وإلّا يُنشئ نسيجاً 1×1 مؤقّتاً ويبدأ التحميل؛ يبقى الإطار السابق معروضاً حتى يجهز.
     */
    private ensure(gl: GLContext, src: string): PoolEntry {
        const existing = this.pool.get(src);
        if (existing) {
            this.pool.delete(src);        // إعادة الإدراج = أحدث استعمالاً (‏Map يحفظ ترتيب الإدراج)
            this.pool.set(src, existing);
            return existing;
        }

        const entry: PoolEntry = {
            tex: createTexture(gl, 1, 1, new Uint8Array([0, 0, 0, 0]), { filter: gl.LINEAR })!,
            w: 1, h: 1, ready: false, gen: ++this.loadGen,
        };
        this.pool.set(src, entry);
        this.evictPool(gl);
        this.loadInto(gl, entry, src);
        return entry;
    }

    /**
     * يطرد الأقدم استعمالاً فوق السعة — **عدا الإطارين المعروضين الآن**.
     *
     * الاستثناء ليس تحسيناً بل شرط صحّة: بلا تثبيتهما قد يُطرد نسيج الإطار الحالي لصالح
     * إحماء إطار بعيد، فتختفي الطبقة لحظةً ثم تعود بعد إعادة رفع — أي بالضبط التلعثم
     * الذي أُنشئت الحلقة لإزالته.
     */
    private evictPool(gl: GLContext) {
        while (this.pool.size > POOL_MAX) {
            let victim: string | null = null;
            for (const key of this.pool.keys()) {
                // displayedUrl مثبَّت أيضاً: هو ما يُرسم فعلاً الآن حين يتأخّر إطار الرابط
                // الحالي، وطردُه يُعيد الاختفاءَ الذي أُصلح للتوّ.
                if (key === this.url || key === this.url2 || key === this.displayedUrl
                    || key === this.previewUrl) continue;
                victim = key; break;                       // الأقدم استعمالاً غير المثبَّت
            }
            if (!victim) break;                            // الكل مثبَّت (لا يحدث عملياً)
            const e = this.pool.get(victim)!;
            this.pool.delete(victim);
            gl.deleteTexture(e.tex);
        }
    }

    private loadInto(gl: GLContext, entry: PoolEntry, src: string, attempt = 0) {
        const gen = entry.gen;
        // ما زال هذا المدخل هو صاحب الرابط، ولم يُطرد، ولم يُبطَل طلبه.
        const isCurrent = () => !this.destroyed && this.pool.get(src) === entry && entry.gen === gen;
        void loadRasterImage(src).then((img) => {
            // قد تصل الصورة بعد إزالة الطبقة (سباق StrictMode/تبديل سريع) — والنسيج
            // حينها محذوف؛ الربط به يرمي INVALID_OPERATION. نتحقّق قبل أي استخدام.
            // كذلك تُهمَل الصورة إن لم يعد طلبها هو الأحدث (تبديل طبقة/إطار أثناء التحميل).
            if (this.destroyed || !isCurrent()) return;
            try {
                // قناع اليابسة 8192×8192، وحدّ MAX_TEXTURE_SIZE على كثير من أجهزة الجوال
                // 4096. الرفع فوق الحدّ **لا يرمي استثناءً** بل يضبط gl.getError() = INVALID_VALUE
                // ويترك النسيج غير مكتمل، فيُعيد أخذ العيّنة منه (0,0,0,1) ⇒ isLand=0 في كل
                // بكسل ⇒ العالم كلّه "بحر": ينهار الفصل الساحلي وتتسرّب القيم عبر السواحل،
                // وتُلغى تمريرة الماء أثرها (لا تحذف شيئاً فتُخفي التضاريس كلياً). وكان
                // onReady يُستدعى رغم ذلك فيُعلن الجهوز كذباً. الآن نُصغّر قبل الرفع ونتحقّق.
                const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
                // ‏.width/.height لا .naturalWidth: الكاش يُرجع ImageBitmap (فكّ خارج
                // الخيط الرئيسي) وقد يُرجع Image احتياطاً — والخاصيتان صحيحتان للطرفين.
                const w = img.width, h = img.height;
                let source: TexImageSource = img;
                if (max > 0 && (w > max || h > max)) {
                    const s = Math.min(max / w, max / h);
                    const cv = document.createElement('canvas');
                    cv.width = Math.max(1, Math.floor(w * s));
                    cv.height = Math.max(1, Math.floor(h * s));
                    const ctx = cv.getContext('2d');
                    if (!ctx) return;
                    ctx.drawImage(img, 0, 0, cv.width, cv.height);
                    source = cv;
                }
                gl.bindTexture(gl.TEXTURE_2D, entry.tex);
                perfSection('RasterHeatmap.texImage2D', () =>
                    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source));
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                /*
                 * `gl.getError()` **فقط عند وجود سبب حقيقي للشكّ.**
                 *
                 * النداء ليس رخيصاً: هو نقطة مزامنة تُجبر الخيط الرئيسي على انتظار تفريغ
                 * خطّ أنابيب الـGPU. في trace للتشغيل ظهر بـ**1.9 ثانية زمن ذاتي (4.2%)**
                 * موزّعة على موقعَي نداء — أي أنه وحده أغلى من كل رفع النُسج الذي يحرسه.
                 *
                 * وما يحرسه لم يعد ممكناً أصلاً في المسار الشائع: الحالة الوحيدة التي
                 * وُضع لها (تجاوز MAX_TEXTURE_SIZE فيبقى النسيج غير مكتمل بصمت) نمنعها
                 * **قبل** الرفع بالتصغير أعلاه. فالفحص يبقى للحالة التي لم نُصغِّر فيها
                 * وكانت الأبعاد قريبة من الحدّ — وهي نادرة ولا تقع أثناء التشغيل.
                 */
                const suspect = max > 0 && (w > max * 0.9 || h > max * 0.9);
                if (suspect && gl.getError() !== gl.NO_ERROR) {
                    console.error(`RasterHeatmapGLLayer(${this.id}): تعذّر رفع ${src} (${w}×${h}, الحدّ ${max})`);
                    return;   // لا نُعلن الجهوز على نسيج غير مكتمل
                }
                entry.w = source === img ? w : (source as HTMLCanvasElement).width;
                entry.h = source === img ? h : (source as HTMLCanvasElement).height;
                entry.ready = true;
                if (src === this.url && !this.firstFrameFired) {
                    this.firstFrameFired = true;
                    this.onFirstFrame?.();
                }
                this.map?.triggerRepaint();
            } catch { /* تجاهل */ }
        }).catch((e) => {
            // ملفّ غير موجود (404): لن يصير موجوداً بإعادة الطلب. كل محاولة إضافية طلب
            // فاشل يزاحم إطاراً موجوداً فعلاً ينتظره العرض.
            if (e instanceof MissingRasterError) {
                // معاينة مفقودة ⇒ النموذج كلّه بلا معاينات (تُولَّد دفعةً واحدة). نسجّل
                // المجلّد فنتوقّف عن طلبها منه نهائياً بدل 404 لكل ساعة على حدة.
                if (src.includes('.preview.')) dirsWithoutPreview.add(dirOf(src));
                else if (src === this.url && !this.firstFrameFired) this.onUnavailable?.();
                return;
            }
            // فشل عابر على الاستضافة: أعد المحاولة ما دام هذا الطلب هو الأحدث.
            //
            // الإطار المعروض والتالي **بلا سقف**: كان السقف محاولتين (‏1.2 ثانية بينهما)،
            // وانقطاعٌ أطول من ذلك يترك الخريطة فارغة إلى أن يغيّر المستخدم الرابط بتحريك
            // الأيام. الآن تعود الألوان وحدها فور عودة الخادم. الانتظار يتضاعف حتى 20 ثانية
            // كي لا يُغرق خادماً متعثّراً. وما عداهما (معاينة/إحماء) يبقى بمحاولتين.
            if (!isCurrent()) return;
            const essential = src === this.url || src === this.url2;
            if (!essential && attempt >= 2) return;
            window.setTimeout(() => {
                if (isCurrent()) this.loadInto(gl, entry, src, attempt + 1);
            }, Math.min(1200 * 2 ** attempt, 20000));
        });
    }

    onRemove(_map: MaplibreMap, gl: GLContext) {
        this.destroyed = true;
        if (this.program) gl.deleteProgram(this.program);
        if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
        // حلقة النُسج كاملةً — وإلّا تسرّبت ~32 ميغابايت VRAM عند كل تركيب/فكّ للطبقة
        // (‏StrictMode وحده يفعلها مرّتين في التطوير، وتبديل النموذج يفعلها في الإنتاج).
        for (const e of this.pool.values()) gl.deleteTexture(e.tex);
        this.pool.clear();
        if (this.rampTexture) gl.deleteTexture(this.rampTexture);
        // القناع مشترك — لا يُحذف هنا.
        this.program = this.quadBuffer = this.rampTexture = this.maskTexture = this.gridMaskTexture = null;
        this.map = this.glRef = null;
    }

    private errorLogged = false;
    /** تُضبط للتمريرة الثانية (البحر فقط) قبل كل رسم — راجع makeSeaPassLayer. */
    private seaOnly = false;
    /** هل أُنشئت تمريرة ماء مرافقة؟ عندها تتخطّى التمريرة الأولى بكسلات البحر. */
    private seaPassActive = false;

    render(gl: GLContext, options: CustomRenderMethodInput) {
        this.seaOnly = false;
        try { this.renderInner(gl, options); }
        catch (e) { if (!this.errorLogged) { console.error(`RasterHeatmapGLLayer(${this.id}) render error:`, e); this.errorLogged = true; } }
    }

    /**
     * طبقة مرافقة تُدرَج **فوق** التضاريس وتعيد رسم الماء وحده من موارد هذه الطبقة نفسها.
     *
     * لماذا وكيل لا نسخة ثانية: نسخة `RasterHeatmapGLLayer` مستقلّة تعني نسيجَي إطار
     * إضافيّين بحجم 1440×721 لكل طبقة لونية (~16 ميغابايت VRAM زائدة) وفكّ ترميز مكرّراً
     * للصور. الوكيل يشارك البرنامج والنسج والحالة كلّها؛ كل ما يضيفه هو نداء رسم ثانٍ
     * بـ u_seaOnly=1 — بلا ذاكرة زائدة وبلا تحميل زائد، وبلا أي احتمال أن يفترق إطار
     * التمريرتين عن بعضهما (وهو ما كان سيحدث لو كانتا نسختين تُحمّلان بشكل مستقلّ).
     */
    makeSeaPassLayer(): CustomLayerInterface {
        const owner = this;
        return {
            id: `${this.id}-sea`,
            type: 'custom' as const,
            renderingMode: '2d' as const,
            // الرايةُ تُرفع عند الإضافة الفعلية لا عند إنشاء الكائن: لو فشل addLayer لأي
            // سبب، تبقى false فلا تتخطّى التمريرة الأولى بكسلات البحر — وإلّا اختفى البحر
            // تماماً (لا أحد يرسمه) وظهرت الخريطة بلون القاعدة وحده.
            onAdd() { owner.seaPassActive = true; },
            onRemove() { owner.seaPassActive = false; },
            render(gl: GLContext, options: CustomRenderMethodInput) {
                if (owner.destroyed || !owner.maskReady) return;   // بلا قناع لا معنى لتمريرة بحر
                owner.seaOnly = true;
                try { owner.renderInner(gl, options); }
                catch { /* الطبقة الأصلية تُسجّل الخطأ */ }
                finally { owner.seaOnly = false; }
            },
        };
    }

    private renderInner(gl: GLContext, options: CustomRenderMethodInput) {
        if (!this.program || !this.loc || !this.quadBuffer || !this.rampTexture) return;
        // حجب ما بعد تبديل النوع: الرابط لم يتغيّر بعد، فالنسيج المتاح يخصّ النوع السابق.
        if (this.typeGuardUrl !== null) {
            if (this.url === this.typeGuardUrl) return;
            this.typeGuardUrl = null;
        }
        /*
         * **لا نُخفي الطبقة أبداً بانتظار إطار.**
         *
         * إن جهز إطار الرابط الحالي رسمناه، وإلّا أبقينا آخر إطار جاهز معروضاً حتى يصل.
         * بدون هذا تختفي الألوان كلياً وتظهر قاعدة الخريطة عارية ثانية أو ثانيتين عند كل
         * إطار يتأخّر — وهو أسوأ بكثير من عرض حقل أقدم بدقائق، لأن العين تقرأ الاختفاء
         * كعطل بينما لا تكاد تميّز فرق ساعة واحدة في حقل حرارة أو رياح.
         *
         * هذا ما كان يفعله الكود قبل حلقة النُسج ضمناً (راية `valueReady` تبقى مرفوعة من
         * الإطار السابق أثناء الرفع الجديد)، وقد أسقطتُه سهواً حين صار الفحص على جهوز
         * الرابط الحالي بالذات. الحلقة تجعل استعادته أنظف: نسيج الإطار السابق ما زال حيّاً
         * فيها فلا يكلّف إبقاؤه شيئاً.
         */
        const curEntry = this.pool.get(this.url);
        if (curEntry?.ready) {
            this.displayedUrl = this.url;
        } else if (!this.displayedUrl || !this.pool.get(this.displayedUrl)?.ready) {
            /*
             * ── المعاينة بديلاً، وبشرط واحد: ألّا يكون هناك إطار معروض أصلاً ──────────
             *
             * الحالتان اللتان تصلان إلى هنا هما بالضبط ما يشكو منه المستخدم: أوّل فتح
             * للموقع، وتبديل النموذج (‏reset يُصفّر displayedUrl لأن شريط ألوان النوع
             * الجديد يعني قيماً مختلفة). في كلتيهما الشاشة **فارغة** حتى يصل ملفّ يقارب
             * الميغابايت، فنعرض المعاينة ريثما يصل.
             *
             * أمّا حين يكون إطارٌ كامل الدقّة معروضاً (سحب الشريط داخل النموذج نفسه) فلا
             * نستبدله بمعاينة ضبابية: إبقاء ساعة مجاورة حادّة أفضل من تمويه الساعة
             * المقصودة. أي أن الوضوح لا يتراجع أبداً — يتقدّم فقط.
             */
            const prev = this.previewUrl ? this.pool.get(this.previewUrl) : undefined;
            if (prev?.ready) {
                this.displayedUrl = this.previewUrl;
            } else {
                return;                               // لا معاينة ولا أصل — لا شيء يُرسم بعد
            }
        }
        const shown = this.pool.get(this.displayedUrl!)!;
        if (this.rampDirty) {
            updateTexture(gl, this.rampTexture, 256, 1, buildColorRamp(this.layerType, this.valueRange));
            this.rampDirty = false;
        }

        gl.useProgram(this.program);
        gl.uniformMatrix4fv(this.loc.uniforms.u_matrix, false, new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>));
        gl.uniform4f(this.loc.uniforms.u_bounds, -180, -90, 180, 90);
        // أبعاد النسيج المعروض فعلاً (قد يكون صُغِّر عند الرفع تحت MAX_TEXTURE_SIZE).
        // الشيدر يبني نافذته بخطوة 1/gridSize، فرقم خاطئ هنا يزيح العيّنات.
        gl.uniform2f(this.loc.uniforms.u_gridSize, shown.w, shown.h);
        gl.uniform1f(this.loc.uniforms.u_opacity, this.opacity);
        gl.uniform1f(this.loc.uniforms.u_zoom, this.map?.getZoom() ?? 0);
        gl.uniform1f(this.loc.uniforms.u_p16, this.p16 ? 1 : 0);
        // الحقول التي تقفز قفزة حقيقية عند خطّ الساحل، فيلزم فصل عيّناتها يابسةً/بحراً كي
        // لا يمتدّ لون أحد الجانبين إلى الآخر عبر خلية شبكة تقع على الحدّ.
        // الرياح منها: احتكاك السطح يخفضها فوق اليابسة بوضوح، وكانت مستثناة فيظهر لون
        // البحر ممتدّاً على الساحل ولون اليابسة في الماء — بينما حدّ Zoom Earth للرياح
        // يتبع الساحل بحدّة (رُصد في لقطات مضيق جبل طارق على نفس النموذج ونفس الساعة).
        // **الفصل الساحلي مُعطَّل (u_useMask = 0) عمداً.**
        //
        // كان يُقصي من نافذة الـ4×4 كل عيّنة على الجانب المخالف للبكسل، كي «لا يخرج اللون
        // خارج اليابسة». لكنه يُنتج ضرراً أكبر ممّا يمنع:
        //
        //  • يخترع قيمة غير موجودة: على مقطع ساحلي حقيقي يتدرّج من 10 إلى 30 كم/س، القيمة
        //    الصحيحة عند الساحل 20.0 — وبالفصل تُقرأ **23.8**.
        //  • يصنع حافّة حادّة: أكبر قفزة بين بكسلين متجاورين **7.53 كم/س** بالفصل مقابل
        //    **0.09** بدونه.
        //  • ويظهر ذلك **مستطيلات** لا منحنى ساحل، لأن مجموعة العيّنات المساهِمة تتبدّل كل
        //    خليّة شبكة (28 كم) لا عند خطّ الساحل.
        //
        // والمراجع التي طُلبت محاكاتها لا تفعل هذا أصلاً: تعرض الحقل متّصلاً وترسم خطّ
        // ساحل حادّاً فوقه. ما يبدو «توقّفاً عند الساحل» عندها هو البيانات نفسها (البرّ
        // والبحر يختلفان فعلاً) لا قصّاً.
        //
        // تمريرتا البرّ/البحر تبقيان — بلا فصل تُنتجان اللون نفسه فيختفي أثر الفاصل بينهما،
        // ووظيفتهما الباقية هي قصر ظلّ التضاريس على اليابسة.
        gl.uniform1f(this.loc.uniforms.u_useMask, 0);
        // يُقرأ كل إطار لأن الحجم لا يُعرف قبل رفع القناع، وقد
        // يُصغَّر عند الرفع. الرقم نفسه في الطبقتين ⇒ نفس المنحنى في اللون والخطّ.
        gl.uniform1f(this.loc.uniforms.u_maskSize, getLandMaskSize(gl));
        /*
         * ── حقل بحري بحت: اليابسة تُرفض بالقناع لا بالقيمة ───────────────────────
         *
         * شذوذ سطح البحر معرَّف على الماء وحده. قيم اليابسة في الراستر ليست بيانات بل
         * حشوٌ ممدود من أقرب ساحل، وُضع كي تصحّ نافذة الاستيفاء 4×4 عند الحدّ (راجع
         * _fill_land في oisst_to_raster.py) — فلو رُسمت لظهرت القارّات ملوّنة بحرارة
         * بحرٍ ليس تحتها.
         *
         * إخفاؤها بالقناع لا بترميز «لا قيمة» في البكسل: أي رقم سنتيّنل داخل المدى
         * (‏0 = -5 °م مثلاً) سيدخل متوسّط النافذة عند الساحل فيسحب لون الماء المجاور
         * نحوه — أي خطّ بارد كاذب على كل ساحل. القناع يفصل **بعد** أخذ العيّنة فلا
         * يلوّث شيئاً.
         *
         * `maskReady` شرط ضروري: بلا قناع محمَّل نرسم كل شيء (يابسة وبحر) بدل ألّا
         * نرسم شيئاً — إطارٌ أو إطاران بقارّات ملوّنة أهون من طبقة تبقى فارغة لو تعثّر
         * تحميل القناع.
         */
        const isSst = this.layerType === 'sst-anomaly' || this.layerType === 'sst-anomaly-rel';
        const seaField = isSst && this.maskReady;
        // البايت 0 = «لا بيانات» في راستر الشذوذ وحده (راجع NODATA_BYTE).
        gl.uniform1f(this.loc.uniforms.u_noData, isSst ? 1 : 0);
        gl.uniform1f(this.loc.uniforms.u_debug, sstDebugEnabled() ? 1 : 0);
        gl.uniform1f(this.loc.uniforms.u_seaOnly, (this.seaOnly || seaField) ? 1 : 0);
        /*
         * `u_landOnly` يبقى كما هو — **ولا يُستثنى الحقل البحري منه**.
         *
         * استثناؤه كان يبدو توفيراً («لا يابسة تُرسم، فلمَ تمريرة يابسة؟») وهو في الحقيقة
         * عطبٌ لوني: عندها ترسم التمريرتان البحرَ كلتاهما، والمزج هنا
         * `ONE / ONE_MINUS_SRC_ALPHA` فالرسم مرّتين يُركّب اللون على نفسه — كل قيمة
         * ألفاها < 1 (وهي أغلب مدى هذا التدرّج: 0.55..0.95) تخرج أغمق وأشبع ممّا يقوله
         * المفتاح. أي خريطة تكذب على مفتاحها في المنطقة التي بُنيت الطبقة لإظهارها.
         *
         * بإبقائه: التمريرة الأولى تحصل على seaOnly=1 **و** landOnly=1 معاً فترفض كل
         * بكسل (البحر بالثانية واليابسة بالأولى) ولا ترسم شيئاً، وتتكفّل تمريرة الماء
         * وحدها بالرسم فوق التضاريس. مرّة واحدة بالضبط، وبلا ظلّ تضاريس على الماء.
         */
        gl.uniform1f(this.loc.uniforms.u_landOnly,
            (!this.seaOnly && this.seaPassActive && this.maskReady) ? 1 : 0);
        // نمزج مع الإطار التالي فقط حين يكون جاهزاً ونسبة المزج > 0 (وإلا الإطار الحالي وحده).
        const cur = shown;                                  // مضمون الجهوز (فُحص في الحارس)
        const nxt = this.url2 ? this.pool.get(this.url2) : undefined;
        // نمزج فقط حين يكون المعروض هو إطار الرابط الحالي فعلاً. إن كنّا نعرض إطاراً أقدم
        // (ريثما يصل الحالي) فمزجه مع إطار "التالي" يقفز فوق إطار كامل — تشويه لا تنعيم.
        const blend = (cur === this.pool.get(this.url) && nxt?.ready && this.blend > 0.001) ? this.blend : 0;
        gl.uniform1f(this.loc.uniforms.u_blend, blend);

        bindTexture(gl, cur.tex, 0); gl.uniform1i(this.loc.uniforms.u_value, 0);
        bindTexture(gl, this.rampTexture, 1); gl.uniform1i(this.loc.uniforms.u_ramp, 1);
        bindTexture(gl, this.maskTexture!, 2); gl.uniform1i(this.loc.uniforms.u_mask, 2);
        // بلا إطار تالٍ جاهز نربط الحالي في الفتحة الثانية أيضاً: u_blend = 0 يُلغي أثره،
        // لكن ترك فتحة بلا نسيج صالح يرمي على بعض التعريضات.
        bindTexture(gl, (nxt?.ready ? nxt.tex : cur.tex), 3); gl.uniform1i(this.loc.uniforms.u_value2, 3);
        bindTexture(gl, this.gridMaskTexture!, 4); gl.uniform1i(this.loc.uniforms.u_gridMask, 4);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.enableVertexAttribArray(this.loc.attributes.a_pos);
        gl.vertexAttribPointer(this.loc.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
        gl.disable(gl.CULL_FACE);
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}
